import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Download, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Lists documents stored for an order (GET /client-documents).
 * Works for admin (admin_token) and client portal (session_token).
 */
export default function ClientOrderDocumentsList({
  orderId,
  backendUrl,
  refreshTrigger = 0,
  compact = false,
  tokenStorageKey = 'admin_token',
  titleKey = 'service_client_uploaded_files',
  emptyTextKey = 'service_client_uploaded_files_empty',
}) {
  const { t } = useTranslation();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!orderId || !backendUrl) return;
    const token = localStorage.getItem(tokenStorageKey);
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${backendUrl}/api/orders/${orderId}/client-documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const msg =
          res.status === 403
            ? t('client_order_doc_load_forbidden')
            : t('client_order_doc_load_fail');
        setError(msg);
        setDocs([]);
        return;
      }
      const data = await res.json();
      setDocs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError(t('client_order_doc_load_fail'));
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [orderId, backendUrl, tokenStorageKey, t]);

  useEffect(() => {
    load();
  }, [load, refreshTrigger]);

  const download = async (docId, filename) => {
    const token = localStorage.getItem(tokenStorageKey);
    if (!token) return;
    try {
      const res = await fetch(
        `${backendUrl}/api/orders/${orderId}/client-documents/${docId}/file`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        toast.error(t('client_order_doc_download_fail'));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error(t('client_order_doc_download_fail'));
    }
  };

  if (!orderId) return null;

  return (
    <div
      className={
        compact
          ? 'space-y-2'
          : 'space-y-2 rounded-lg border border-gray-200 bg-white p-4'
      }
    >
      <p className="text-sm font-semibold text-gray-800">{t(titleKey)}</p>
      {loading && (
        <p className="text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('client_order_doc_loading')}
        </p>
      )}
      {error && !loading && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}
      {!loading && !error && docs.length === 0 && (
        <p className="text-sm text-gray-500">{t(emptyTextKey)}</p>
      )}
      {!loading && docs.length > 0 && (
        <ul className="space-y-2">
          {docs.map((doc) => (
            <li
              key={doc.id}
              className="flex flex-wrap items-center justify-between gap-2 text-sm bg-gray-50 border border-gray-200 rounded-md px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <FileText className="h-4 w-4 shrink-0 text-gray-500" />
                <span className="truncate" title={doc.original_filename}>
                  {doc.original_filename}
                </span>
                {doc.uploaded_by_role === 'admin' && (
                  <span className="text-[10px] uppercase tracking-wide text-amber-800 bg-amber-100 rounded px-1.5 py-0.5 shrink-0">
                    {t('client_order_doc_badge_admin')}
                  </span>
                )}
                {doc.uploaded_by_role === 'client' && tokenStorageKey === 'session_token' && (
                  <span className="text-[10px] uppercase tracking-wide text-green-800 bg-green-100 rounded px-1.5 py-0.5 shrink-0">
                    {t('client_order_doc_badge_yours')}
                  </span>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => download(doc.id, doc.original_filename)}
              >
                <Download className="w-4 h-4 mr-1" />
                {t('client_order_doc_download')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
